/**
 * The admin-override refusal dialog (DNKADM-0396) — ONE dialog for every reason an admin
 * write was refused, a dialog and never a toast.
 *
 * It generalizes DNKADM-0386 U1's coach double-book dialog. That one answered a single
 * question ("this coach is already booked"); DNKAPI-0549 added three more gates that an
 * admin override used to remove in silence — a bay with no capacity left
 * (`RESOURCE_CONFLICT`), a coach off shift (`STAFF_OUTSIDE_HOURS`) and a coach past an
 * appointment cap (`STAFF_OVER_CAP`) — and **several of those can apply to one request**.
 * So the server returns every applicable reason in ONE body and this shows them in ONE
 * dialog, rather than walking the desk through three round-trips.
 *
 * **One reader feeds this dialog.** The hosts that call an admin override path — change
 * coach/bay on the calendar (`…/assignment`) and in the pending queue (`…/details`), edit
 * time (`…/details`), reschedule (`PUT …/{uuid}`) and, since DNKAPI-0636 D-06, confirm (both
 * hosts, `POST …/confirm`) — catch refusals through `parseOverrideRefusal`, which reads
 * every reason of a multi-reason body. Create-on-behalf has no dialog (DNKADM-0457):
 * `AdminReservationCreateRequest` carries no acknowledge field, so it can never be refused
 * for an override reason.
 *
 * This mounts with what the SERVER said:
 *  - the title is the envelope `message` — for a multi-reason refusal that is the
 *    highest-precedence reason, by the server's own fixed order;
 *  - each reason is the server's own plain-words sentence. **Nothing is reconstructed
 *    client-side**, and no reason is paraphrased.
 *  - a reason's blocking booking(s) come from what the server named for THAT reason —
 *    customer first name, service, time range, bay — each linking to that reservation on
 *    the calendar (`/reservations?reservation=<uuid>`, the existing deep link).
 *  - **A reason with no bookings says nothing about bookings.** `resourceConflicts` is
 *    `@Nullable` on the wire and is an explicit `null` on both staff-schedule reasons:
 *    nothing is "in the way" of a coach who simply is not working. The parse keeps absent
 *    (`undefined`) and empty (`[]`) distinct and this renders neither as a "no conflicts"
 *    claim (the DNKADM-0395 U3 lesson: never render a null as a positive statement).
 *  - **Change it** (primary) hands control back to the picker the action came from
 *    (`onChangeIt`); the host focuses its field. Deliberately NOT "Choose another coach" —
 *    the reason may be a bay, and a coach-specific label would be wrong.
 *  - **Book anyway** is disabled until the acknowledgement is ticked, then the host resends
 *    the IDENTICAL request with every acknowledge flag THIS refusal named, and only those
 *    (`onBookAnyway` + `overrideAcknowledgements`) — so a single "proceed anyway" can never
 *    acknowledge a decision the admin was not shown. It is **hidden entirely** when the
 *    refusal named no flag this console can send, rather than offering an override that
 *    would resend the identical body and refuse identically.
 *  - **Cancel** closes the dialog and changes nothing (`onCancel`).
 *
 * `STAFF_WILL_CHANGE` (DNKAPI-0546 / DNKADM-0393 U5) keeps its own dialog and is NOT folded
 * in: "this change would hand the booking to a different coach" is a different question with
 * a different answer, and its `acknowledgeStaffChange` is never set from here.
 *
 * **Every backend string is scrubbed HERE, at the render boundary — the title included.** The
 * title usually arrives already scrubbed (`serverMessage`), but `parseOverrideRefusal` falls back
 * to the first block's RAW `message` when the envelope's own is blank, so the fallback is the
 * unsafe path — and it is the branch least likely to be exercised by hand. A brand leak in a
 * dialog title is rendered text on the desk's screen, which the white-label rule forbids outright.
 * Scrubbing twice is harmless; scrubbing once too few is a defect.
 *
 * Times render through the shared 12-hour formatter, as written on the wire — a conflict's
 * `startTime`/`endTime` are offset strings in the blocking reservation's location zone, the
 * same contract as the drawer. Mobile: full-screen below `sm`, the actions wrap, and the
 * rows shrink rather than overflow — the front desk runs an iPad.
 */
import { useId, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { BookingConflict, ConflictReason, ConflictRefusal } from '@/api/reservations';
import { canBookAnyway } from '@/api/reservations';
import { Icon, resolveTokens } from '@/theme';
import { isoClockRangeAsIs } from '@/utils/clockTime';
import { scrubBrand } from '@/utils/whitelabel';

/**
 * The acknowledgement tick. Generic on purpose: one refusal may carry several reasons, and
 * naming only one of them here (as DNKADM-0386 U1's coach-specific wording did) would state
 * a consequence that is not the whole truth. The reasons themselves are listed directly
 * above it, in the server's own words.
 */
export const ACKNOWLEDGE_LABEL = 'I understand, and want to book anyway.';

export interface OverrideConflictDialogProps {
  open: boolean;
  /** The refusal as `parseOverrideRefusal` read it — never reconstructed here. */
  refusal: ConflictRefusal;
  /** Primary — return to the picker the action came from (the host focuses its field). */
  onChangeIt: () => void;
  /** Resend the identical request with every acknowledge flag this refusal named. */
  onBookAnyway: () => void;
  /** Close without changing anything. Also fires when a calendar link is followed. */
  onCancel: () => void;
  /** True while the acknowledged resend is in flight — every action is held. */
  submitting?: boolean;
}

/** "Ann · Lesson" — the server's first name and service, with neutral fallbacks for explicit nulls. */
function whoAndWhat(c: BookingConflict): string {
  const who = scrubBrand(c.customerFirstName?.trim() || 'A customer');
  const what = scrubBrand(c.serviceName?.trim() || 'Booking');
  return `${who} · ${what}`;
}

/** "10:30 AM – 11:30 AM · Bay 2" — the wire's own wall clock, then the bay when there is one. */
function whenAndWhere(c: BookingConflict): string {
  const range = isoClockRangeAsIs(c.startTime, c.endTime);
  const bay = c.resourceName?.trim();
  return bay ? `${range} · ${scrubBrand(bay)}` : range;
}

/** The calendar route; a link from a dialog raised over it keeps the calendar's view. */
const CALENDAR_PATH = '/reservations';

/**
 * A booking's calendar link. From the calendar itself, the link carries the calendar's
 * current view params (DNKADM-0430: board, date, filters) so following it moves only the
 * drawer, as it did when that view was component state. From anywhere else it is the plain
 * `/reservations?reservation=<uuid>` deep link.
 */
function calendarLinkFor(uuid: string, pathname: string, search: string): string {
  const params = new URLSearchParams(pathname === CALENDAR_PATH ? search : '');
  params.set('reservation', uuid);
  return `${CALENDAR_PATH}?${params.toString()}`;
}

/**
 * The booking(s) the server named for one reason. Renders NOTHING when it named none
 * (`undefined`) or named an empty list — in neither case may the console claim there are no
 * bookings in the way; it simply says nothing about them.
 */
function ConflictRows({
  conflicts,
  onFollowLink,
}: {
  conflicts: BookingConflict[] | undefined;
  onFollowLink: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const { pathname, search } = useLocation();
  if (conflicts === undefined || conflicts.length === 0) return null;
  return (
    <Stack
      component="ul"
      aria-label="Bookings in the way"
      sx={{ listStyle: 'none', p: 0, m: 0, gap: 1 }}
    >
      {conflicts.map((c, index) => (
        <Stack
          component="li"
          key={c.reservationUuid ?? `conflict-${index}`}
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
            px: 1.5,
            py: 1.25,
            border: `1px solid ${t.lineSoft}`,
            borderRadius: 1.5,
            bgcolor: t.surface2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
            <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{whoAndWhat(c)}</Typography>
            <Typography variant="body2" sx={{ color: t.text2, overflowWrap: 'anywhere' }}>
              {whenAndWhere(c)}
            </Typography>
          </Box>
          {c.reservationUuid !== null && (
            <Link
              component={RouterLink}
              to={calendarLinkFor(c.reservationUuid, pathname, search)}
              onClick={onFollowLink}
              sx={{ fontSize: 14, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              View on calendar
            </Link>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

export function OverrideConflictDialog({
  open,
  refusal,
  onChangeIt,
  onBookAnyway,
  onCancel,
  submitting = false,
}: OverrideConflictDialogProps) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const titleId = `${ids}-title`;
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [acknowledged, setAcknowledged] = useState(false);
  const overridable = canBookAnyway(refusal);
  // One reason → its sentence IS the title, so only its bookings are listed below (the
  // DNKADM-0386 U1 layout, unchanged). Several → every reason is spelled out.
  const reasons: ConflictReason[] = refusal.reasons;
  const single = reasons.length === 1 ? reasons[0] : undefined;

  function close() {
    if (!submitting) onCancel();
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1 }}>
        <Icon name="event_busy" sx={{ fontSize: 24, color: t.warn, mt: 0.25, flexShrink: 0 }} />
        <Typography component="span" sx={{ fontSize: 18, fontWeight: 500, overflowWrap: 'anywhere' }}>
          {scrubBrand(refusal.message)}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {single !== undefined ? (
          <ConflictRows conflicts={single.conflicts} onFollowLink={close} />
        ) : (
          <Stack
            component="ul"
            aria-label="Why this was refused"
            sx={{ listStyle: 'none', p: 0, m: 0, gap: 2 }}
          >
            {reasons.map((reason, index) => (
              <Box component="li" key={reason.code ?? `reason-${index}`}>
                <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere', mb: 1 }}>
                  {scrubBrand(reason.message)}
                </Typography>
                <ConflictRows conflicts={reason.conflicts} onFollowLink={close} />
              </Box>
            ))}
          </Stack>
        )}

        {overridable && (
          <FormControlLabel
            sx={{ mt: 2, alignItems: 'flex-start', mr: 0 }}
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
                disabled={submitting}
                sx={{ mt: -1 }}
              />
            }
            label={ACKNOWLEDGE_LABEL}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5, flexWrap: 'wrap', gap: 1 }}
      >
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2, mr: 'auto' }}>
          Cancel
        </Button>
        {overridable && (
          <Button
            variant="outlined"
            color="warning"
            onClick={onBookAnyway}
            disabled={!acknowledged || submitting}
          >
            {submitting ? 'Booking…' : 'Book anyway'}
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<Icon name="edit" />}
          onClick={onChangeIt}
          disabled={submitting}
        >
          Change it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
